import { useMutation, useQueryClient } from 'react-query'
import { AxiosRequestConfig } from 'axios'
import toast from 'react-hot-toast'
import { nfdUpdatePartial, UpdatePartialRequestBody } from 'src/api/api-client'
import type { TransactionsArray } from 'types/api'
import useWallet from 'hooks/useWallet'
import useErrorToast from 'hooks/useErrorToast'

export const patchPartialUpdate = async (
  name: string,
  body: UpdatePartialRequestBody,
  wallet: ReturnType<typeof useWallet>,
  ssr?: boolean,
  options?: AxiosRequestConfig
) => {
  const { data } = await nfdUpdatePartial(name, body, {
    ...(ssr && { headers: { origin: process.env.NEXT_PUBLIC_BASE_URL as string } }),
    ...options
  })
  const transactionsArray = JSON.parse(data) as TransactionsArray
  const signedTransactions = await wallet.signEncodedTransactions(transactionsArray)

  return wallet.sendRawTransactions(signedTransactions)
}

export type ToastComponentProps = { name?: string; txnId?: string }
type ToastComponent = ({ name, txnId }: ToastComponentProps) => JSX.Element
type Toast = string | ToastComponent

type UsePatchPartialUpdateArgs = {
  loading?: Toast
  success?: Toast
}

type UseMutationArgs = {
  name: string
  body: UpdatePartialRequestBody
}

export default function usePatchPartialUpdate(options?: UsePatchPartialUpdateArgs) {
  const { loading = 'Waiting on user to sign...', success = 'Success!' } = { ...options }

  const wallet = useWallet()
  const queryClient = useQueryClient()
  const handleError = useErrorToast()

  return useMutation(
    ({ name, body }: UseMutationArgs) => {
      return patchPartialUpdate(name, body, wallet)
    },
    {
      onMutate: (variables) => {
        console.info('Sending transaction...')
        const toastMsg = typeof loading === 'string' ? loading : loading({ name: '', txnId: '' })

        const toastId = toast.loading(toastMsg)

        return { name: variables.name, toastId }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSuccess: (data, _variables, context: any) => {
        console.info(`Transaction ${data.id} confirmed in round ${data['confirmed-round']}`)

        const toastMsg =
          typeof success === 'string' ? success : success({ name: context.name, txnId: data.id })

        toast.success(toastMsg, {
          id: context.toastId,
          duration: 5000 // 5 seconds
        })

        return queryClient.invalidateQueries(['name'])
      },
      onError: (error, _variables, context) => {
        handleError(error, { id: context.toastId })
        return queryClient.invalidateQueries(['name'])
      }
    }
  )
}
